import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ElaBot } from "./ElaBot";
import { usePathname } from "@/lib/navigation";
import {
  ChevronDown,
  ChevronLeft,
  Clock,
  Maximize2,
  MessageSquare,
  Minimize2,
  Plus,
  X,
} from "lucide-react";
import { ChatRuntimeRoot, useChatSession } from "@myela/ai-core-react/runtime";
import { AuiIf, ThreadPrimitive } from "@myela/ai-core-react/primitives";
import { useBots } from "@myela/ai-core-react/bots";
import { BOTS_API, repository } from "./myela-repository";
import { chatStore } from "./myela-store";
import {
  type ChatHistoryItem,
  ExpandedMessages,
  ExpandedSuggestions,
  MiniMessages,
  MiniSuggestions,
  RuntimeComposer,
  TypingDots,
  useThreadHistory,
} from "./chat-runtime-views";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Loader } from "@/components/common/Loader";
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@myela/ui";
import { IconTooltip } from "@/components/ui/icon-tooltip";

/* ═══════════════════════════════════════════════
   Context (floating-variant expand state)
   ═══════════════════════════════════════════════ */

interface ChatExpandState {
  expanded: boolean;
  setExpanded: (v: boolean) => void;
  collapse: () => void;
  /** A question from Home's ask bar, sent once the chat's runtime is up. */
  pendingAsk: string | null;
  /** Open the chat full and send `text` (Home's ask bar). */
  ask: (text: string) => void;
  clearAsk: () => void;
  /** The chat is on for this person (MYELA_ENABLED, not a GoDaddy merchant). */
  enabled: boolean;
}
const ChatExpandCtx = createContext<ChatExpandState>({
  expanded: false,
  setExpanded: () => {},
  collapse: () => {},
  pendingAsk: null,
  ask: () => {},
  clearAsk: () => {},
  enabled: false,
});
export const useChatExpand = () => useContext(ChatExpandCtx);
export { ChatExpandCtx };

/* ═══════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════ */

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const PANEL_TRANSITION = [
  `top 520ms ${EASE}`,
  `left 520ms ${EASE}`,
  `width 520ms ${EASE}`,
  `height 520ms ${EASE}`,
  `border-radius 420ms ease`,
  `box-shadow 360ms ease`,
  `opacity 220ms ease`,
  `transform 380ms ${EASE}`,
  `border-color 200ms ease`,
].join(", ");

interface ChatAccess {
  /** The signed-in user's one bot (slug); null until it loads, or when chat is unavailable. */
  bot: string | null;
  status: "loading" | "ready" | "unavailable" | "impersonating";
  retry: () => void;
}

/**
 * The bot host returns exactly one bot per user (staff vs agent lead), so the
 * first list entry is the bot — there is no picker. The host refuses chat while
 * an admin is viewing as someone else; the auth flag skips that doomed request.
 */
function useChatAccess(): ChatAccess {
  const { isImpersonating } = useAuth();
  const query = useBots(repository, chatStore, { enabled: !isImpersonating });
  const retry = () => void query.refetch();
  if (isImpersonating || query.error?.message.includes("impersonation_unsupported")) {
    return { bot: null, status: "impersonating", retry };
  }
  if (query.isError) return { bot: null, status: "unavailable", retry };
  if (!query.data) return { bot: null, status: "loading", retry };
  const bot = query.data[0]?.slug ?? null;
  return { bot, status: bot ? "ready" : "unavailable", retry };
}

/** Mounts the runtime once the bot is known; until then children render without it. */
function ChatRuntime({ bot, children }: { bot: string | null; children: ReactNode }) {
  if (!bot) return <>{children}</>;
  return (
    <ChatRuntimeRoot
      key={bot}
      repository={repository}
      store={chatStore}
      bot={bot}
      tools={[]}
      basePath={BOTS_API}
    >
      {children}
    </ChatRuntimeRoot>
  );
}

/** Stand-in for the thread + composer while the bot loads or chat is unavailable. */
function ChatNotice({ access }: { access: ChatAccess }) {
  if (access.status === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader size={32} />
      </div>
    );
  }
  const impersonating = access.status === "impersonating";
  return (
    <Empty className="flex-1">
      <EmptyHeader>
        <EmptyTitle>
          {impersonating ? "Chat unavailable" : "Failed to load Ela"}
        </EmptyTitle>
        <EmptyDescription>
          {impersonating
            ? "Chat is unavailable while viewing as another user."
            : "The assistant could not be reached."}
        </EmptyDescription>
      </EmptyHeader>
      {impersonating ? null : (
        <EmptyContent>
          <Button variant="outline" size="sm" onClick={access.retry}>
            Retry
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}

/* ═══════════════════════════════════════════════
   ChatPanel
   ═══════════════════════════════════════════════ */

export interface ChatPanelProps {
  variant: "floating" | "embedded";
  /** Floating-only: closes the open panel when the sidebar collapses. */
  collapsing?: boolean;
  /** Embedded-only: which surface is currently shown; gates the reveal gesture. */
  chatMode?: "chat" | "dashboard";
  /** Embedded-only: fired by a firm downward gesture at the chat bottom (and the
   * hint button) to reveal the dashboard. */
  onRevealDashboard?: () => void;
  /** Embedded-only: optional greeting line above the empty-state heading. */
  greeting?: string;
}

export default function ChatPanel(props: ChatPanelProps) {
  if (props.variant === "embedded") {
    return <EmbeddedChat {...props} />;
  }
  return <FloatingChat {...props} />;
}

/* ═══════════════════════════════════════════════
   Embedded variant (sticky dashboard chat)
   ═══════════════════════════════════════════════ */

function EmbeddedChat(props: ChatPanelProps) {
  const access = useChatAccess();
  return (
    <ChatRuntime bot={access.bot}>
      <EmbeddedChatBody {...props} access={access} />
    </ChatRuntime>
  );
}

function EmbeddedChatBody(props: ChatPanelProps & { access: ChatAccess }) {
  const { chatMode = "chat", onRevealDashboard, greeting, access } = props;
  const chatRef = useRef<HTMLDivElement>(null);

  // Reveal gesture: the chat and dashboard are two discrete layers (Dashboard
  // slides the dashboard in). Scroll priority stays with the chat — while the
  // message list can still scroll down it always scrolls (even when the pointer
  // sits over the composer/header outside the viewport), and the dashboard never
  // opens. Only once the chat is at its end does a downward scroll reveal the
  // dashboard. The momentum tail of the same fling that reached the bottom is
  // absorbed (via the inter-event gap) so a fast scroll through the messages
  // doesn't carry straight into the dashboard; a fresh scroll then reveals it
  // immediately — no attempt counting, so quick scrolls aren't dropped.
  useEffect(() => {
    const container = chatRef.current;
    if (!container || chatMode !== "chat" || !onRevealDashboard) return;

    const SETTLE_GAP_MS = 120; // a fresh gesture starts after this quiet gap
    const LINE_HEIGHT = 40; // wheel line-mode delta → px

    const viewportAtBottom = (vp: HTMLElement) =>
      vp.scrollTop + vp.clientHeight >= vp.scrollHeight - 2;

    let lastTs = 0;

    const drive = (deltaY: number, e: Event) => {
      const now = performance.now();
      const gap = now - lastTs;
      lastTs = now;

      if (deltaY <= 0) return; // upward inside the chat: nothing to reveal

      // Empty threads render no `.chat-scroll` element → null → non-scrollable.
      const vp = container.querySelector<HTMLElement>(".chat-scroll");
      const scrollable = !!vp && vp.scrollHeight - vp.clientHeight > 2;

      // Chat still has room: keep scroll priority on the chat. When the pointer
      // is over the viewport the browser scrolls it natively; when it's outside
      // (composer/header) scroll it manually so the chat still moves first.
      if (vp && scrollable && !viewportAtBottom(vp)) {
        if (!vp.contains(e.target as Node)) {
          e.preventDefault();
          vp.scrollTop += deltaY;
        }
        return;
      }

      // At the end (or nothing to scroll). Absorb the momentum tail of the fling
      // that just bottomed out; a fresh downward scroll reveals the dashboard.
      e.preventDefault();
      if (scrollable && gap < SETTLE_GAP_MS) return;
      onRevealDashboard();
    };

    const onWheel = (e: WheelEvent) => {
      let d = e.deltaY;
      if (e.deltaMode === 1) d *= LINE_HEIGHT;
      else if (e.deltaMode === 2) d *= container.clientHeight;
      drive(d, e);
    };

    let lastTouchY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastTouchY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? 0;
      const deltaY = lastTouchY - y;
      lastTouchY = y;
      drive(deltaY, e);
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
    };
  }, [chatMode, onRevealDashboard]);

  return (
    <div
      className="sticky top-0 h-full bg-card text-foreground"
      style={{ zIndex: 0 }}
    >
      <div
        ref={chatRef}
        className="h-full flex flex-col"
        style={{ willChange: "opacity, filter, transform" }}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-6 py-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <ElaBot size={44} still />
            <div className="text-base font-medium">Ela</div>
          </div>
          {access.bot ? (
            <div className="flex items-center gap-1">
              <ThreadMenu />
            </div>
          ) : null}
        </div>

        {/* Content */}
        {access.bot ? (
          <ThreadPrimitive.Root className="flex-1 flex flex-col min-h-0">
            <AuiIf condition={({ thread }) => !thread.isEmpty}>
              <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto min-h-0 scroll-smooth chat-scroll">
                <div className="max-w-[860px] mx-auto px-8 py-6 flex flex-col gap-5">
                  <ExpandedMessages />
                  <TypingDots indent />
                </div>
              </ThreadPrimitive.Viewport>
              <div className="shrink-0 py-4">
                <div className="max-w-[860px] mx-auto px-8">
                  <RuntimeComposer
                    placeholder="What should we look into?"
                    size="comfortable"
                  />
                  <div className="mt-2 text-center text-xs text-muted-foreground">
                    Ela can help navigate and explain, but cannot execute transactions.
                  </div>
                </div>
              </div>
            </AuiIf>
            <AuiIf condition={({ thread }) => thread.isEmpty}>
              <div className="flex-1 flex flex-col items-center justify-center px-8 pt-10">
                <div className="flex flex-col items-center gap-5 w-full max-w-[860px]">
                  <ChatEmptyIntro greeting={greeting} />
                  <div className="w-full max-w-[640px]">
                    <RuntimeComposer
                      placeholder="What should we look into?"
                      size="comfortable"
                    />
                  </div>
                  <ExpandedSuggestions />
                </div>
              </div>
            </AuiIf>
          </ThreadPrimitive.Root>
        ) : (
          <ChatNotice access={access} />
        )}

        {/* Scroll hint */}
        {onRevealDashboard ? (
          <div className="flex justify-center pb-5 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRevealDashboard}
            >
              <ChevronDown className="h-4 w-4" />
              Scroll to see dashboard
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Floating variant (draggable bubble + popup + expanded)
   ═══════════════════════════════════════════════ */

type Corner = "tl" | "tr" | "bl" | "br";

function FloatingChat(props: ChatPanelProps) {
  const { collapsing = false } = props;
  const access = useChatAccess();

  const pathname = usePathname();
  const { expanded, setExpanded } = useChatExpand();
  const [open, setOpen] = useState(false);
  const [corner, setCorner] = useState<Corner>("br");
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  // Mirrors the pointer being held down so the bubble shows `grabbing` from the
  // press (not only after the 3px drag threshold) — matches the pre-runtime feel.
  const [pressing, setPressing] = useState(false);
  const [contentRect, setContentRect] = useState<{
    left: number;
    top: number;
    w: number;
    h: number;
  } | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ active: false, moved: false, sx: 0, sy: 0 });

  const mode: "closed" | "popup" | "expanded" = expanded
    ? "expanded"
    : open
      ? "popup"
      : "closed";

  // Measure content area
  useEffect(() => {
    const el = measureRef.current?.parentElement;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContentRect({ left: r.left, top: r.top, w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Close panel when the sidebar collapses — an external-system sync (the
  // collapse is driven by AppLayout), which is the sanctioned effect use the
  // set-state-in-effect heuristic can't distinguish from a cascading render.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (collapsing) setOpen(false);
  }, [collapsing]);

  // Escape key
  useEffect(() => {
    if (mode === "closed") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (expanded) setExpanded(false);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, expanded, setExpanded]);

  // Drag logic (corner-snap)
  const BUBBLE = 40;

  function nearestCorner(x: number, y: number): Corner {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const horiz = x + BUBBLE / 2 < vw / 2 ? "l" : "r";
    const vert = y + BUBBLE / 2 < vh / 2 ? "t" : "b";
    return (vert + horiz) as Corner;
  }
  function onBubblePointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    drag.current = { active: true, moved: false, sx: e.clientX, sy: e.clientY };
    setPressing(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onDragMove(e: React.PointerEvent) {
    if (!drag.current.active) return;
    e.stopPropagation();
    const dx = e.clientX - drag.current.sx;
    const dy = e.clientY - drag.current.sy;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.current.moved = true;
    if (drag.current.moved) {
      setDragPos({ x: e.clientX - BUBBLE / 2, y: e.clientY - BUBBLE / 2 });
    }
  }
  function onBubblePointerUp(e: React.PointerEvent) {
    e.stopPropagation();
    const wasDrag = drag.current.moved;
    drag.current.active = false;
    setPressing(false);
    if (!wasDrag) {
      // Defer the open state change to the next frame so the bubble stays
      // hit-testable while the browser dispatches the trailing click event.
      // Otherwise pointer-events flips to "none" before the click fires, and
      // the click falls through to whatever sits beneath the bubble — e.g.
      // an application list row whose onClick navigates to a detail page.
      requestAnimationFrame(() => setOpen(true));
      return;
    }
    setCorner(nearestCorner(e.clientX - BUBBLE / 2, e.clientY - BUBBLE / 2));
    setDragPos(null);
  }

  // Panel geometry. INSET + PW are viewport-aware so the popup fits on phones:
  // a hard `PW = 400` + 24px inset overflows any viewport narrower than ~448px
  // (the bubble would open off-screen on a 375px iPhone). On narrow screens we
  // shrink the inset and clamp the width to `vw - 2*INSET` so the popup always
  // stays inside the viewport with a 12px gutter on each side.
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const isNarrow = vw < 480;
  const INSET = isNarrow ? 12 : 24;
  const GAP = 12;
  const PW = Math.min(400, vw - INSET * 2);
  const PH = Math.min(560, vh - INSET * 2 - BUBBLE - GAP);
  const rect = contentRect ?? { left: 240, top: 56, w: vw - 240, h: vh - 56 };

  const bubbleStyle: React.CSSProperties = dragPos
    ? { top: dragPos.y, left: dragPos.x }
    : corner === "tl"
      ? { top: INSET, left: INSET }
      : corner === "tr"
        ? { top: INSET, right: INSET }
        : corner === "bl"
          ? { bottom: INSET, left: INSET }
          : { bottom: INSET, right: INSET };

  function popupGeomForCorner(c: Corner): React.CSSProperties {
    const offsetFromBubble = INSET + BUBBLE + GAP;
    switch (c) {
      case "tl":
        return { top: offsetFromBubble, left: INSET };
      case "tr":
        return { top: offsetFromBubble, right: INSET };
      case "bl":
        return { bottom: offsetFromBubble, left: INSET };
      case "br":
        return { bottom: offsetFromBubble, right: INSET };
    }
  }
  const POPUP_TRANSFORM_ORIGIN: Record<Corner, string> = {
    tl: "0% 0%",
    tr: "100% 0%",
    bl: "0% 100%",
    br: "100% 100%",
  };

  const popupBase = popupGeomForCorner(corner);
  const transformOrigin = POPUP_TRANSFORM_ORIGIN[corner];

  let geom: React.CSSProperties;
  if (mode === "expanded") {
    geom = {
      top: rect.top,
      left: rect.left,
      width: rect.w,
      height: rect.h,
      borderRadius: 0,
      opacity: 1,
      transform: "scale(1)",
      boxShadow: "none",
      borderColor: "transparent",
    };
  } else if (mode === "popup") {
    geom = {
      ...popupBase,
      width: PW,
      height: PH,
      borderRadius: 20,
      opacity: 1,
      transform: "scale(1)",
      boxShadow:
        "0 1px 0 oklch(0 0 0 / 0.04), 0 24px 60px -20px oklch(0 0 0 / 0.25), 0 8px 20px -10px oklch(0 0 0 / 0.15)",
      borderColor: "var(--myela-border)",
    };
  } else {
    geom = {
      ...popupBase,
      width: PW,
      height: PH,
      borderRadius: 20,
      opacity: 0,
      transform: "scale(0.4)",
      boxShadow:
        "0 1px 0 oklch(0 0 0 / 0.04), 0 24px 60px -20px oklch(0 0 0 / 0.25), 0 8px 20px -10px oklch(0 0 0 / 0.15)",
      borderColor: "transparent",
    };
  }

  // Home has its own ask bar (staff) or embedded chat, so no bubble there.
  const onDashboard = pathname === "/";
  const bubbleVisible = mode === "closed" && !onDashboard;

  return (
    <>
      {/* Hover/active lift for the chat bubble. Lives in a <style> tag so we
          don't need JS event handlers — works for any pointer device, doesn't
          break under SSR. Keep the selector unique (`.chat-bubble`). */}
      <style>{`
        .chat-bubble {
          transition:
            transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 220ms ease;
        }
        .chat-bubble:hover {
          transform: translateY(-2px) scale(1.04);
          box-shadow:
            0 18px 38px -10px oklch(0 0 0 / 0.55),
            0 6px 14px -4px oklch(0 0 0 / 0.30);
        }
        .chat-bubble:active {
          transform: translateY(0) scale(0.96);
        }
      `}</style>

      <div
        ref={measureRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          pointerEvents: "none",
        }}
      />

      {/* Draggable bubble (corner-snap).
       *
       * Visibility recipe — clean premium FAB pattern:
       *  - Solid Ink-900 dark fill. The MSD logo is solid Signal orange
       *    (#D98E3D), which reads beautifully against the dark fill on every
       *    page background (light Myelin, white cards, even darker hero
       *    sections).
       *  - No border. The dark fill IS the boundary, so we don't get the
       *    "puffy outlined sticker" look earlier iterations had.
       *  - Two-stop drop shadow only — one deep, one tight contact — so the
       *    bubble looks elevated, not haloed.
       *  - Hover lift handled via the `chat-bubble` className (CSS above)
       *    rather than per-event JS handlers.
       */}
      <button
        type="button"
        onPointerDown={onBubblePointerDown}
        onPointerMove={onDragMove}
        onPointerUp={onBubblePointerUp}
        onClick={(e) => e.stopPropagation()}
        className="grid place-items-center select-none bg-primary!"
        style={{
          position: "fixed",
          zIndex: 50,
          ...bubbleStyle,
          width: BUBBLE,
          height: BUBBLE,
          borderRadius: 999,
          background: "var(--accent-100)",
          color: "white",
          border: 0,
          boxShadow:
            "0 14px 30px -8px oklch(0 0 0 / 0.45), 0 4px 10px -2px oklch(0 0 0 / 0.22)",
          cursor: pressing ? "grabbing" : "pointer",
          opacity: bubbleVisible ? 1 : 0,
          transform: bubbleVisible ? "scale(1)" : "scale(0)",
          transition: dragPos
            ? `opacity 280ms ease, transform 380ms ${EASE}, box-shadow 220ms ease`
            : `opacity 280ms ease, transform 380ms ${EASE}, top 280ms ${EASE}, left 280ms ${EASE}, right 280ms ${EASE}, bottom 280ms ${EASE}, box-shadow 220ms ease`,
          touchAction: "none",
          pointerEvents: bubbleVisible ? "auto" : "none",
        }}
        aria-label="Open AI Chat"
      >
        <ElaBot size={40} />
      </button>
      <div
        className="text-foreground"
        style={{
          position: "fixed",
          zIndex: 70,
          background: "var(--myela-card)",
          border: "1px solid",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transformOrigin,
          willChange: "top, left, width, height, border-radius",
          pointerEvents: mode === "closed" ? "none" : "auto",
          transition: PANEL_TRANSITION,
          ...geom,
        }}
      >
        {/* One runtime instance wraps BOTH the popup and expanded trees so the
            session, thread list, and streaming state survive the morph. */}
        <ChatRuntime bot={access.bot}>
          <FloatingChatBody
            mode={mode}
            expanded={expanded}
            setOpen={setOpen}
            setExpanded={setExpanded}
            access={access}
          />
        </ChatRuntime>
      </div>
    </>
  );
}

interface FloatingBodyProps {
  mode: "closed" | "popup" | "expanded";
  expanded: boolean;
  setOpen: (v: boolean) => void;
  setExpanded: (v: boolean) => void;
  access: ChatAccess;
}

/** Sends Home's ask-bar question once the runtime is mounted, then forgets it. */
function SendPendingAsk() {
  const { pendingAsk, clearAsk } = useChatExpand();
  const { send } = useChatSession();
  useEffect(() => {
    if (!pendingAsk) return;
    send(pendingAsk);
    clearAsk();
  }, [pendingAsk, send, clearAsk]);
  return null;
}

function FloatingChatBody({
  mode,
  expanded,
  setOpen,
  setExpanded,
  access,
}: FloatingBodyProps) {
  const [view, setView] = useState<"chat" | "history">("chat");
  const showChat = () => setView("chat");

  return (
    <>
      {access.status === "ready" && <SendPendingAsk />}
      {/* Mini (popup) content */}
      <div
        style={{
          display: expanded ? "none" : "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className="border-b"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            flexShrink: 0,
            userSelect: "none",
          }}
        >
          <div className="flex items-center gap-2.5 pointer-events-none">
            <ElaBot size={40} still />
            <div className="text-sm font-medium">Ela</div>
          </div>
          <div className="flex items-center gap-0.5">
            {!access.bot ? null : view === "history" ? (
              <IcoBtn onClick={showChat} title="Back">
                <ChevronLeft className="size-[15px]" />
              </IcoBtn>
            ) : (
              <>
                <IcoBtn onClick={() => setView("history")} title="History">
                  <MessageSquare className="size-[15px]" />
                </IcoBtn>
                <MiniNewChatButton />
              </>
            )}
            <IcoBtn
              onClick={() => {
                setOpen(false);
                setExpanded(true);
              }}
              title="Expand"
            >
              <Maximize2 className="size-[15px]" />
            </IcoBtn>
            <IcoBtn onClick={() => setOpen(false)} title="Close">
              <X className="size-[15px]" />
            </IcoBtn>
          </div>
        </div>

        {!access.bot ? (
          <ChatNotice access={access} />
        ) : view === "history" ? (
          <MiniHistory onDone={showChat} />
        ) : (
          <ThreadPrimitive.Root
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <ThreadPrimitive.Viewport
              className="chat-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "14px 14px 6px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  flex: 1,
                }}
              >
                <AuiIf condition={({ thread }) => thread.isEmpty}>
                  <div className="flex-1 flex flex-col items-center justify-center gap-3">
                    <ElaBot size={64} still />
                    <p className="m-0 text-sm text-muted-foreground">
                      How can I help?
                    </p>
                    <MiniSuggestions />
                  </div>
                </AuiIf>
                <AuiIf condition={({ thread }) => !thread.isEmpty}>
                  <MiniMessages />
                  <TypingDots />
                </AuiIf>
              </div>
            </ThreadPrimitive.Viewport>
            <div className="border-t" style={{ padding: 10, flexShrink: 0 }}>
              <RuntimeComposer
                placeholder="What should we look into?"
                size="compact"
                autoFocusKey={mode === "popup" ? "popup" : undefined}
              />
            </div>
          </ThreadPrimitive.Root>
        )}
      </div>

      {/* Expanded content */}
      <div
        style={{
          display: expanded ? "flex" : "none",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <div className="flex items-center justify-between px-6 py-3 shrink-0">
          <div className="flex items-center gap-2.5">
            <ElaBot size={44} still />
            <div className="text-base font-medium">Ela</div>
          </div>
          <div className="flex items-center gap-1">
            {access.bot ? <ThreadMenu onDone={showChat} /> : null}
            <IconTooltip label="Collapse" className="z-[80]">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setExpanded(false)}
                aria-label="Collapse"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </IconTooltip>
          </div>
        </div>

        {!access.bot ? (
          <ChatNotice access={access} />
        ) : (
          <ThreadPrimitive.Root className="flex-1 flex flex-col min-h-0">
            <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto min-h-0 scroll-smooth chat-scroll">
              <div className="mx-auto flex min-h-full max-w-[860px] flex-col px-8">
                <AuiIf condition={({ thread }) => thread.isEmpty}>
                  <div
                    className="flex-1 flex flex-col items-center justify-center pt-10 pb-8"
                    style={{
                      animation: "fadeUp 360ms cubic-bezier(0.22,1,0.36,1) both",
                    }}
                  >
                    <div className="flex flex-col items-center gap-5 w-full max-w-215">
                      <ChatEmptyIntro />
                      <ExpandedSuggestions />
                    </div>
                  </div>
                </AuiIf>
                <AuiIf condition={({ thread }) => !thread.isEmpty}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 20,
                      paddingTop: 24,
                      paddingBottom: 8,
                    }}
                  >
                    <ExpandedMessages />
                    <TypingDots indent />
                  </div>
                </AuiIf>
              </div>
            </ThreadPrimitive.Viewport>

            <div className="shrink-0 py-4">
              <div className="mx-auto max-w-[860px] px-8">
                <RuntimeComposer
                  placeholder="Ask anything..."
                  size="comfortable"
                  autoFocusKey={mode === "expanded" ? "expanded" : undefined}
                />
                <div className="mt-2 text-center text-xs text-muted-foreground">
                  Ela can help navigate and explain, but cannot execute transactions.
                </div>
              </div>
            </div>
          </ThreadPrimitive.Root>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   Shared sub-components
   ═══════════════════════════════════════════════ */

/**
 * Compact empty-state intro for the full-screen chat surfaces (embedded +
 * expanded). Logo and greeting sit on a single row — the logo in the same
 * accent tile used by the header bar — above the "How can I help?" prompt, so
 * the intro reads as one tight block rather than a tall stacked hero.
 */
function ChatEmptyIntro({ greeting }: { greeting?: string }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center gap-2.5">
        <ElaBot size={48} still />
        <h2 className="m-0 text-2xl font-semibold tracking-tight">
          {greeting || "Ela"}
        </h2>
      </div>
    </div>
  );
}

/** "New" + "History" header controls; `onDone` runs after either switches thread. */
function ThreadMenu({ onDone }: { onDone?: () => void }) {
  const { items, activeId, selectThread, newThread } = useThreadHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);

  useHistoryDismiss(historyOpen, setHistoryOpen, historyRef);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          newThread();
          setHistoryOpen(false);
          onDone?.();
        }}
      >
        <Plus className="h-4 w-4" /> New
      </Button>
      <div className="relative" ref={historyRef}>
        <Button
          variant={historyOpen ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setHistoryOpen((v) => !v)}
        >
          <Clock className="h-4 w-4" /> History
        </Button>
        {historyOpen && (
          <HistoryDropdown
            items={items}
            activeId={activeId}
            onSelect={(remoteId) => {
              selectThread(remoteId);
              setHistoryOpen(false);
              onDone?.();
            }}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </div>
    </>
  );
}

function MiniNewChatButton() {
  const { threadList } = useChatSession();
  return (
    <IcoBtn onClick={() => threadList?.switchToNewThread()} title="New chat">
      <Plus className="size-[15px]" />
    </IcoBtn>
  );
}

/** Closes the history dropdown on any outside click while it is open. */
function useHistoryDismiss(
  open: boolean,
  setOpen: (v: boolean) => void,
  ref: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, setOpen, ref]);
}

function IcoBtn({
  children,
  onClick,
  title,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
}) {
  return (
    <IconTooltip label={title} className="z-[80]">
      <Button
        type="button"
        variant={active ? "secondary" : "ghost"}
        size="icon-sm"
        onClick={onClick}
        aria-label={title}
      >
        {children}
      </Button>
    </IconTooltip>
  );
}

function MiniHistory({ onDone }: { onDone: () => void }) {
  const { items, activeId, selectThread } = useThreadHistory();
  const onSelect = (remoteId: string) => {
    selectThread(remoteId);
    onDone();
  };
  return (
    <div className="flex-1 overflow-y-auto chat-scroll">
      {items.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
          <MessageSquare className="size-8 opacity-30" />
          <p className="m-0 text-sm">No conversations yet</p>
        </div>
      ) : (
        <div className="divide-y py-1">
          {items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t.remoteId)}
              className={cn(
                "block w-full cursor-pointer px-3.5 py-2.5 text-left transition-colors",
                activeId === t.id ? "bg-muted" : "hover:bg-muted/50",
              )}
            >
              <div
                className={cn(
                  "truncate text-sm font-medium",
                  activeId === t.id ? "text-primary" : "text-foreground",
                )}
              >
                {t.title || "New chat"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryDropdown({
  items,
  activeId,
  onSelect,
  onClose,
}: {
  items: ChatHistoryItem[];
  activeId: string | null;
  onSelect: (remoteId: string) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="absolute top-full right-0 z-20 mt-2 flex max-h-100 w-80 flex-col overflow-hidden rounded-lg border bg-card shadow-xl"
      style={{ animation: "fadeUp 200ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <span className="text-sm font-medium">Chat History</span>
        <IconTooltip label="Close history" className="z-[80]">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close history"
          >
            <X className="h-4 w-4" />
          </Button>
        </IconTooltip>
      </div>
      <div className="flex-1 divide-y overflow-y-auto chat-scroll">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Clock className="mx-auto mb-2 size-7 text-muted-foreground" />
            <p className="m-0 text-sm text-muted-foreground">
              No conversations yet
            </p>
          </div>
        ) : (
          items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t.remoteId)}
              className={cn(
                "block w-full cursor-pointer px-4 py-2.5 text-left transition-colors",
                activeId === t.id ? "bg-muted" : "hover:bg-muted/50",
              )}
            >
              <div
                className={cn(
                  "truncate text-sm font-medium",
                  activeId === t.id ? "text-primary" : "text-foreground",
                )}
              >
                {t.title || "New chat"}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
